import React, { useState } from 'react';

// ============================================================
// MRVN Development Length Calculator - ACI 318-19
// Covers: Straight bar tension, Hooked bar tension,
//         Compression development, Tension lap splices,
//         Compression lap splices
// ============================================================

const COLORS = {
  bgMain: '#0a0f1e',
  bgCard: '#0d1325',
  accent: '#3b82f6',
  textDark: '#e2e8f0',
  textMid: '#94a3b8',
  textMuted: '#64748b',
  border: 'rgba(255,255,255,0.1)',
  success: '#22c55e',
  danger: '#ef4444',
  warning: '#f59e0b',
};

// ============================================================
// REBAR DATA
// ============================================================

const REBAR = {
  '3':  { num: 3,  db: 0.375, area: 0.11 },
  '4':  { num: 4,  db: 0.500, area: 0.20 },
  '5':  { num: 5,  db: 0.625, area: 0.31 },
  '6':  { num: 6,  db: 0.750, area: 0.44 },
  '7':  { num: 7,  db: 0.875, area: 0.60 },
  '8':  { num: 8,  db: 1.000, area: 0.79 },
  '9':  { num: 9,  db: 1.128, area: 1.00 },
  '10': { num: 10, db: 1.270, area: 1.27 },
  '11': { num: 11, db: 1.410, area: 1.56 },
  '14': { num: 14, db: 1.693, area: 2.25 },
  '18': { num: 18, db: 2.257, area: 4.00 },
};

// ============================================================
// CALCULATION FUNCTIONS - ACI 318-19
// ============================================================

function calcStraightTension(inputs) {
  const bar = REBAR[inputs.barSize];
  const db = bar.db;
  const fy = inputs.fy;
  const fc = inputs.fc;
  const sqrtFc = Math.min(Math.sqrt(fc), 100); // 25.4.1.4: sqrt(f'c) <= 100 psi

  // Modification factors - Table 25.4.2.5
  const lambda = inputs.lightweight ? 0.75 : 1.0;

  let psi_g = 1.0;
  if (inputs.fy <= 60000) psi_g = 1.0;
  else if (inputs.fy <= 80000) psi_g = 1.15;
  else psi_g = 1.3;

  let psi_e = 1.0;
  if (inputs.coating === 'epoxy') {
    if (inputs.clearCover < 3 * db || inputs.clearSpacing < 6 * db) psi_e = 1.5;
    else psi_e = 1.2;
  }

  const psi_s = (bar.num <= 6) ? 0.8 : 1.0;
  const psi_t = inputs.topBar ? 1.3 : 1.0;

  // Per ACI 25.4.2.5: product Psi_t * Psi_e need not exceed 1.7
  const psi_te_product = Math.min(psi_t * psi_e, 1.7);

  let ld_simplified, ld_general, method;
  const steps = [];

  // Simplified method - Table 25.4.2.3
  const hasMinCoverSpacing = inputs.useSimplified;

  if (hasMinCoverSpacing) {
    const isCategory1 = inputs.simplifiedCategory === 'category1';

    if (isCategory1) {
      if (bar.num <= 6) {
        ld_simplified = (fy * psi_te_product * psi_g / (25 * lambda * sqrtFc)) * db;
        steps.push({ label: 'Simplified (Table 25.4.2.3)', eq: '\u2113d = (fy\u00b7\u03c8t\u00b7\u03c8e\u00b7\u03c8g / (25\u00b7\u03bb\u00b7\u221af\'c))\u00b7db', ref: 'ACI 25.4.2.3, Category 1, No. 6 and smaller' });
      } else {
        ld_simplified = (fy * psi_te_product * psi_g / (20 * lambda * sqrtFc)) * db;
        steps.push({ label: 'Simplified (Table 25.4.2.3)', eq: '\u2113d = (fy\u00b7\u03c8t\u00b7\u03c8e\u00b7\u03c8g / (20\u00b7\u03bb\u00b7\u221af\'c))\u00b7db', ref: 'ACI 25.4.2.3, Category 1, No. 7 and larger' });
      }
    } else {
      if (bar.num <= 6) {
        ld_simplified = (3 * fy * psi_te_product * psi_g / (50 * lambda * sqrtFc)) * db;
        steps.push({ label: 'Simplified (Table 25.4.2.3)', eq: '\u2113d = (3\u00b7fy\u00b7\u03c8t\u00b7\u03c8e\u00b7\u03c8g / (50\u00b7\u03bb\u00b7\u221af\'c))\u00b7db', ref: 'ACI 25.4.2.3, Category 2 (Other), No. 6 and smaller' });
      } else {
        ld_simplified = (3 * fy * psi_te_product * psi_g / (40 * lambda * sqrtFc)) * db;
        steps.push({ label: 'Simplified (Table 25.4.2.3)', eq: '\u2113d = (3\u00b7fy\u00b7\u03c8t\u00b7\u03c8e\u00b7\u03c8g / (40\u00b7\u03bb\u00b7\u221af\'c))\u00b7db', ref: 'ACI 25.4.2.3, Category 2 (Other), No. 7 and larger' });
      }
    }
  }

  // General method - Eq. 25.4.2.4a
  const cb = inputs.cb || Math.min(inputs.clearCover + db / 2, (inputs.clearSpacing + db) / 2);
  let Ktr = 0;
  if (inputs.useKtr && inputs.Atr && inputs.s_trans && inputs.n_bars) {
    Ktr = (40 * inputs.Atr) / (inputs.s_trans * inputs.n_bars);
  }
  const confinement = Math.min((cb + Ktr) / db, 2.5);

  ld_general = (3 / 40) * (fy / (lambda * sqrtFc)) * (psi_te_product * psi_s * psi_g / confinement) * db;
  steps.push({ label: 'General (Eq. 25.4.2.4a)', eq: 'ld = (3/40) * (fy / (Lam * sqrt(fc))) * (Psi_t * Psi_e * Psi_s * Psi_g) / ((cb + Ktr)/db) * db', ref: 'ACI 25.4.2.4' });

  // Use simplified if selected, otherwise general
  let ld;
  if (inputs.useSimplified && ld_simplified) {
    ld = ld_simplified;
    method = 'Simplified (Table 25.4.2.3)';
  } else {
    ld = ld_general;
    method = 'General (Eq. 25.4.2.4a)';
  }

  // Minimum: 12 in. per 25.4.2.1(b)
  const ld_min = 12;
  const ld_final = Math.max(ld, ld_min);

  return {
    type: 'Straight Bar - Tension',
    ld: ld_final,
    ld_calc: ld,
    ld_min,
    method,
    factors: { psi_t, psi_e, psi_s, psi_g, lambda, cb: cb, Ktr, confinement },
    bar, fy, fc, db, sqrtFc,
    steps,
    ld_simplified: ld_simplified || null,
    ld_general,
  };
}

function calcHookedTension(inputs) {
  const bar = REBAR[inputs.barSize];
  const db = bar.db;
  const fy = inputs.fy;
  const fc = inputs.fc;
  const sqrtFc = Math.min(Math.sqrt(fc), 100);

  const lambda = inputs.lightweight ? 0.75 : 1.0;
  const psi_e = inputs.coating === 'epoxy' ? 1.2 : 1.0;

  // Confining reinforcement factor - Table 25.4.3.2
  let psi_r = 1.6; // default: other
  if (bar.num <= 11) {
    if (inputs.hookConfined) psi_r = 1.0;
  }

  // Location factor
  let psi_o = 1.25; // default: other
  if (bar.num <= 11) {
    if (inputs.hookInsideColumn || inputs.hookSideCover >= 6 * db) psi_o = 1.0;
  }

  // Concrete strength factor
  let psi_c = 1.0;
  if (fc < 6000) {
    psi_c = fc / 15000 + 0.6;
  }

  const ldh = (fy * psi_e * psi_r * psi_o * psi_c * db) / (55 * lambda * sqrtFc);

  const ldh_min_8db = 8 * db;
  const ldh_min_6in = 6;
  const ldh_final = Math.max(ldh, ldh_min_8db, ldh_min_6in);

  return {
    type: 'Standard Hook - Tension',
    ld: ldh_final,
    ld_calc: ldh,
    ld_min: Math.max(ldh_min_8db, ldh_min_6in),
    ld_min_label: `max(8db = ${ldh_min_8db.toFixed(2)} in., 6 in.)`,
    method: 'ACI 25.4.3.1(a)',
    factors: { psi_e, psi_r, psi_o, psi_c, lambda },
    bar, fy, fc, db, sqrtFc,
    steps: [{ label: 'Hook Development (25.4.3.1)', eq: 'ldh = (fy * Psi_e * Psi_r * Psi_o * Psi_c * db) / (55 * Lam * sqrt(fc))', ref: 'ACI 25.4.3.1(a)' }],
  };
}

function calcCompression(inputs) {
  const bar = REBAR[inputs.barSize];
  const db = bar.db;
  const fy = inputs.fy;
  const fc = inputs.fc;
  const sqrtFc = Math.min(Math.sqrt(fc), 100);
  const lambda = inputs.lightweight ? 0.75 : 1.0;
  const psi_r = inputs.compressionConfined ? 0.75 : 1.0;

  const ldc_a = (psi_r * fy * db) / (50 * lambda * sqrtFc);
  const ldc_b = 0.0003 * fy * psi_r * db;
  const ldc_calc = Math.max(ldc_a, ldc_b);
  const ldc_min = 8;
  const ldc_final = Math.max(ldc_calc, ldc_min);

  return {
    type: 'Compression Development',
    ld: ldc_final,
    ld_calc: ldc_calc,
    ld_a: ldc_a,
    ld_b: ldc_b,
    ld_min: ldc_min,
    method: 'ACI 25.4.9.2',
    factors: { psi_r, lambda },
    bar, fy, fc, db, sqrtFc,
    steps: [
      { label: 'Eq. 25.4.9.2(a)', eq: 'ldc = (Psi_r * fy * db) / (50 * Lam * sqrt(fc))', ref: 'ACI 25.4.9.2(a)' },
      { label: 'Eq. 25.4.9.2(b)', eq: 'ldc = 0.0003 * fy * Psi_r * db', ref: 'ACI 25.4.9.2(b)' },
    ],
  };
}

function calcTensionSplice(inputs) {
  // First calculate straight tension development length
  const devResult = calcStraightTension(inputs);
  const ld = devResult.ld_calc; // use calculated, not min-governed

  const spliceClass = inputs.spliceClass || 'B';
  const multiplier = spliceClass === 'A' ? 1.0 : 1.3;
  const lst_calc = multiplier * ld;
  const lst_min = 12;
  const lst_final = Math.max(lst_calc, lst_min);

  return {
    type: 'Tension Lap Splice',
    ld: lst_final,
    ld_calc: lst_calc,
    ld_dev: ld,
    ld_min: lst_min,
    spliceClass,
    multiplier,
    method: 'ACI 25.5.2.1 (Table 25.5.2.1)',
    factors: devResult.factors,
    bar: devResult.bar, fy: devResult.fy, fc: devResult.fc, db: devResult.db, sqrtFc: devResult.sqrtFc,
    steps: [
      ...devResult.steps,
      { label: `Class ${spliceClass} Splice`, eq: `lst = ${multiplier.toFixed(1)} x ld`, ref: 'ACI 25.5.2.1, Table 25.5.2.1' },
    ],
    devResult,
  };
}

function calcCompressionSplice(inputs) {
  const bar = REBAR[inputs.barSize];
  const db = bar.db;
  const fy = inputs.fy;
  const fc = inputs.fc;

  if (bar.num > 11) {
    return { type: 'Compression Lap Splice', error: 'Lap splices not permitted for bars larger than No. 11 (ACI 25.5.5.2)', bar, fy, fc, db };
  }

  let lsc;
  const lst_min = 12;

  if (fy <= 60000) {
    lsc = 0.0005 * fy * db;
  } else if (fy <= 80000) {
    lsc = (0.0009 * fy - 24) * db;
  } else {
    // fy > 80,000: use larger of compression formula and tension splice per 25.5.5.1(c)
    lsc = (0.0009 * fy - 24) * db;
    // Note: also check against tension splice -- for simplicity using compression formula
  }

  let lsc_adjusted = lsc;
  if (fc < 3000) {
    lsc_adjusted = lsc * (4 / 3); // increase by one-third per 25.5.5.1
  }

  const lsc_final = Math.max(lsc_adjusted, lst_min);

  return {
    type: 'Compression Lap Splice',
    ld: lsc_final,
    ld_calc: lsc_adjusted,
    ld_unadjusted: lsc,
    ld_min: lst_min,
    lowFcAdjusted: fc < 3000,
    method: 'ACI 25.5.5.1',
    factors: {},
    bar, fy, fc, db,
    steps: [
      { label: fy <= 60000 ? 'fy <= 60,000 psi' : 'fy > 60,000 psi',
        eq: fy <= 60000 ? 'lsc = 0.0005 * fy * db' : 'lsc = (0.0009 * fy - 24) * db',
        ref: 'ACI 25.5.5.1' },
    ],
  };
}

// ============================================================
// PDF GENERATION
// ============================================================

function loadJsPDF() {
  return new Promise((resolve, reject) => {
    if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.2/jspdf.umd.min.js';
    script.onload = () => resolve(window.jspdf.jsPDF);
    script.onerror = () => reject(new Error('Failed to load jsPDF'));
    document.head.appendChild(script);
  });
}

async function generateDevLengthPDF(result, inputs, projectInfo) {
  let jsPDF;
  try { jsPDF = await loadJsPDF(); } catch (err) { alert('Failed to load PDF library.'); return; }

  const doc = new jsPDF('p', 'pt', 'letter');
  const W = 612, H = 792;
  const M = { left: 54, right: 54, top: 54, bottom: 54 };
  const CW = W - M.left - M.right;
  let y = M.top;
  let pageNum = 1;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const navy = [30, 58, 95];
  const darkNavy = [15, 36, 64];
  const blue = [59, 130, 246];
  const textDark = [30, 41, 59];
  const textMid = [71, 85, 105];
  const textLight = [148, 163, 184];
  const borderColor = [203, 213, 225];
  const bgLight = [241, 245, 249];

  const drawHeader = () => {
    doc.setFillColor(...navy);
    doc.rect(0, 0, W, 72, 'F');
    doc.setFillColor(...darkNavy);
    doc.roundedRect(M.left, 16, 40, 40, 6, 6, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('M', M.left + 13, 43);
    doc.setFontSize(16);
    doc.text('MRVN', M.left + 50, 35);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(180, 200, 220);
    doc.text('Structural Engineering Platform', M.left + 50, 48);
    doc.setTextColor(180, 200, 220);
    doc.setFontSize(8);
    doc.text(today, W - M.right, 30, { align: 'right' });
    if (projectInfo.projectNumber) doc.text('Project: ' + projectInfo.projectNumber, W - M.right, 42, { align: 'right' });
    doc.text('Page ' + pageNum, W - M.right, 54, { align: 'right' });
  };

  const drawFooter = () => {
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.5);
    doc.line(M.left, H - 40, W - M.right, H - 40);
    doc.setFontSize(7);
    doc.setTextColor(...textLight);
    doc.text('Generated by MRVN \u2014 mrvnai.com', M.left, H - 28);
    doc.text('Page ' + pageNum, W - M.right, H - 28, { align: 'right' });
    if (projectInfo.engineerName) doc.text('Engineer: ' + projectInfo.engineerName, W / 2, H - 28, { align: 'center' });
  };

  const drawSectionTitle = (title) => {
    doc.setFillColor(...bgLight);
    doc.rect(M.left, y, CW, 22, 'F');
    doc.setFillColor(...blue);
    doc.rect(M.left, y, 3, 22, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text(title, M.left + 12, y + 15);
    y += 32;
  };

  const drawRow = (label, value, indent) => {
    const x = indent ? M.left + 20 : M.left + 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMid);
    doc.text(label, x, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text(String(value), M.left + 280, y);
    y += 16;
  };

  const checkPage = (needed) => {
    if (y + needed > H - M.bottom - 50) {
      drawFooter();
      doc.addPage();
      pageNum++;
      drawHeader();
      y = 90;
    }
  };

  // PAGE 1
  drawHeader();
  y = 86;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textDark);
  doc.text('Development Length Calculation', M.left, y);
  y += 16;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text(result.type, M.left, y);
  y += 14;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textMid);
  doc.text('ACI 318-19, Chapter 25 \u2022 Imperial units (psi, in.)', M.left, y);
  y += 10;
  if (projectInfo.projectName) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text('Project: ' + projectInfo.projectName, M.left, y);
    y += 14;
  }
  y += 8;

  // Input Summary
  drawSectionTitle('Input Parameters');
  drawRow('Bar Size:', '#' + result.bar.num + ' (db = ' + result.db.toFixed(3) + ' in.)');
  drawRow('Bar Diameter (db):', result.db.toFixed(3) + ' in.');
  drawRow('Yield Strength (fy):', (result.fy / 1000).toFixed(0) + ' ksi (' + result.fy.toLocaleString() + ' psi)');
  drawRow('Concrete Strength (f\'c):', (result.fc / 1000).toFixed(1) + ' ksi (' + result.fc.toLocaleString() + ' psi)');
  drawRow('\u221Af\'c (capped at 100 psi):', result.sqrtFc ? result.sqrtFc.toFixed(2) + ' psi' : 'N/A');
  y += 8;

  // Modification Factors
  checkPage(150);
  drawSectionTitle('Modification Factors (ACI 318-19)');

  const f = result.factors;
  if (f.lambda !== undefined) drawRow('Lightweight (Lam.):', f.lambda.toFixed(2) + (f.lambda < 1 ? ' (Lightweight concrete)' : ' (Normalweight)'));
  if (f.psi_t !== undefined) drawRow('Casting Position (Psi_t):', f.psi_t.toFixed(2) + (f.psi_t > 1 ? ' (Top bar)' : ' (Other)'));
  if (f.psi_e !== undefined) drawRow('Coating (Psi_e):', f.psi_e.toFixed(2) + (f.psi_e > 1 ? ' (Epoxy coated)' : ' (Uncoated)'));
  if (f.psi_s !== undefined) drawRow('Size (Psi_s):', f.psi_s.toFixed(2) + (f.psi_s < 1 ? ' (No. 6 and smaller)' : ' (No. 7 and larger)'));
  if (f.psi_g !== undefined) drawRow('Grade (Psi_g):', f.psi_g.toFixed(2));
  if (f.psi_r !== undefined) drawRow('Confining Reinf. (Psi_r):', f.psi_r.toFixed(2));
  if (f.psi_o !== undefined) drawRow('Location (Psi_o):', f.psi_o.toFixed(2));
  if (f.psi_c !== undefined) drawRow('Concrete Strength (Psi_c):', f.psi_c.toFixed(2));
  if (f.confinement !== undefined) {
    drawRow('cb:', f.cb.toFixed(3) + ' in.');
    drawRow('Ktr:', f.Ktr.toFixed(3));
    drawRow('(cb + Ktr)/db:', f.confinement.toFixed(3) + (f.confinement >= 2.5 ? ' (capped at 2.5)' : ''));
  }
  y += 8;

  // Calculation
  checkPage(150);
  drawSectionTitle('Calculation');

  result.steps.forEach(step => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text(step.label, M.left + 10, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMid);
    doc.text(step.eq, M.left + 20, y);
    y += 12;
    doc.setFontSize(8);
    doc.setTextColor(...textLight);
    doc.text('Ref: ' + step.ref, M.left + 20, y);
    y += 18;
  });

  // Intermediate values
  if (result.ld_simplified) {
    drawRow('ld (Simplified):', result.ld_simplified.toFixed(2) + ' in.');
  }
  if (result.ld_general) {
    drawRow('ld (General):', result.ld_general.toFixed(2) + ' in.');
  }
  if (result.ld_a !== undefined) {
    drawRow('ldc from Eq. (a):', result.ld_a.toFixed(2) + ' in.');
    drawRow('ldc from Eq. (b):', result.ld_b.toFixed(2) + ' in.');
  }
  if (result.ld_dev !== undefined) {
    drawRow('ld (development):', result.ld_dev.toFixed(2) + ' in.');
    drawRow('Splice multiplier:', result.multiplier.toFixed(1) + 'x (Class ' + result.spliceClass + ')');
  }
  y += 8;

  // Final Result
  checkPage(80);
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(M.left, y, CW, 50, 4, 4, 'F');
  doc.setFillColor(...blue);
  doc.rect(M.left, y, 4, 50, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textDark);
  doc.text('Required ' + (result.type.includes('Splice') ? 'Splice' : 'Development') + ' Length:', M.left + 16, y + 20);
  doc.setFontSize(16);
  doc.setTextColor(...blue);
  doc.text(result.ld.toFixed(2) + ' in.  (' + (result.ld / 12).toFixed(2) + ' ft)', M.left + 16, y + 40);
  y += 60;

  // Governing provision
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textMid);
  if (result.ld_calc < result.ld_min) {
    doc.text('Note: Minimum length of ' + result.ld_min + ' in. governs.', M.left + 10, y);
  } else {
    doc.text('Calculated length governs (> minimum of ' + result.ld_min + ' in.)', M.left + 10, y);
  }
  y += 14;
  doc.text('Method: ' + result.method, M.left + 10, y);

  drawFooter();

  const filename = projectInfo.projectName
    ? projectInfo.projectName.replace(/[^a-zA-Z0-9]/g, '_') + '_Dev_Length.pdf'
    : 'Development_Length.pdf';
  doc.save(filename);
}

// ============================================================
// COMPONENT STYLES
// ============================================================

const styles = {
  outerWrap: { background: COLORS.bgMain, minHeight: '100vh' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '2rem', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', color: COLORS.textDark, lineHeight: 1.6, minHeight: '100vh', background: COLORS.bgMain },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem', background: '#080c18', margin: '-2rem -2rem 2rem -2rem', padding: '1.5rem 2rem', borderBottom: '1px solid rgba(30,58,95,0.3)' },
  headerTitle: { fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9', margin: 0, letterSpacing: '-0.02em' },
  headerSub: { fontSize: '0.85rem', color: COLORS.textMuted, marginTop: '0.25rem' },
  backBtn: { padding: '0.55rem 1.25rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: COLORS.textDark, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  card: { background: COLORS.bgCard, border: '1px solid ' + COLORS.border, borderRadius: '10px', padding: '1.5rem', marginBottom: '1rem' },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.75rem', marginTop: '1.5rem' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: COLORS.textMid, display: 'block', marginBottom: '0.35rem' },
  input: { width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.04)', border: '1px solid ' + COLORS.border, borderRadius: '6px', color: COLORS.textDark, fontSize: '0.9rem', fontFamily: 'inherit', boxSizing: 'border-box' },
  select: { width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.04)', border: '1px solid ' + COLORS.border, borderRadius: '6px', color: COLORS.textDark, fontSize: '0.9rem', fontFamily: 'inherit', boxSizing: 'border-box', appearance: 'auto' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column' },
  checkbox: { display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: COLORS.textMid, padding: '0.25rem 0' },
  resultBox: { background: 'rgba(59,130,246,0.08)', border: '2px solid rgba(59,130,246,0.3)', borderRadius: '10px', padding: '1.5rem', textAlign: 'center', marginTop: '1rem' },
  resultLabel: { fontSize: '0.85rem', color: COLORS.textMid, marginBottom: '0.25rem' },
  resultValue: { fontSize: '2.2rem', fontWeight: 800, color: COLORS.accent, letterSpacing: '-0.02em' },
  resultUnit: { fontSize: '0.9rem', color: COLORS.textMid },
  resultSecondary: { fontSize: '1rem', color: COLORS.textMid, marginTop: '0.25rem' },
  infoBox: { background: 'rgba(30,58,95,0.1)', border: '1px solid rgba(30,58,95,0.25)', borderRadius: '8px', padding: '1rem', fontSize: '0.85rem', color: COLORS.textMid, borderLeft: '3px solid ' + COLORS.accent },
  factorRow: { display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid ' + COLORS.border, fontSize: '0.85rem' },
  btn: (variant) => ({
    padding: '0.6rem 1.5rem',
    background: variant === 'primary' ? COLORS.accent : variant === 'success' ? '#16a34a' : variant === 'navy' ? '#1e3a5f' : 'rgba(255,255,255,0.08)',
    border: variant === 'ghost' ? '1px solid ' + COLORS.border : 'none',
    borderRadius: '6px',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    fontFamily: 'inherit',
  }),
  tabRow: { display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '1px solid ' + COLORS.border, paddingBottom: '0', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  tab: (active) => ({
    padding: '0.6rem 1.25rem',
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid ' + COLORS.accent : '2px solid transparent',
    color: active ? COLORS.accent : COLORS.textMuted,
    fontWeight: active ? 700 : 500,
    fontSize: '0.88rem', whiteSpace: 'nowrap',
    cursor: 'pointer',
    fontFamily: 'inherit',
  }),
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function DevLengthCalculator({ onBack }) {
  const [calcType, setCalcType] = useState('straight_tension');
  const [result, setResult] = useState(null);

  // Common inputs
  const [barSize, setBarSize] = useState('5');
  const [fy, setFy] = useState(60000);
  const [fc, setFc] = useState(4000);
  const [lightweight, setLightweight] = useState(false);
  const [coating, setCoating] = useState('uncoated');
  const [topBar, setTopBar] = useState(false);

  // Straight tension specific
  const [useSimplified, setUseSimplified] = useState(true);
  const [simplifiedCategory, setSimplifiedCategory] = useState('category1');
  const [clearCover, setClearCover] = useState(1.5);
  const [clearSpacing, setClearSpacing] = useState(3.0);
  const [useKtr, setUseKtr] = useState(false);
  const [Atr, setAtr] = useState(0.22);
  const [sTrans, setSTrans] = useState(12);
  const [nBars, setNBars] = useState(3);

  // Hook specific
  const [hookConfined, setHookConfined] = useState(false);
  const [hookInsideColumn, setHookInsideColumn] = useState(false);
  const [hookSideCover, setHookSideCover] = useState(2.5);

  // Compression specific
  const [compressionConfined, setCompressionConfined] = useState(false);

  // Splice specific
  const [spliceClass, setSpliceClass] = useState('B');

  // PDF project info
  const [projectName, setProjectName] = useState('');
  const [engineerName, setEngineerName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');

  const calcTypes = [
    { id: 'straight_tension', label: 'Straight Bar (Tension)' },
    { id: 'hook_tension', label: 'Standard Hook (Tension)' },
    { id: 'compression', label: 'Compression' },
    { id: 'tension_splice', label: 'Tension Lap Splice' },
    { id: 'compression_splice', label: 'Compression Lap Splice' },
  ];

  const calculate = () => {
    const baseInputs = { barSize, fy, fc, lightweight, coating, topBar, clearCover, clearSpacing, useSimplified, simplifiedCategory, useKtr, Atr, s_trans: sTrans, n_bars: nBars, cb: null };

    let res;
    switch (calcType) {
      case 'straight_tension':
        res = calcStraightTension(baseInputs);
        break;
      case 'hook_tension':
        res = calcHookedTension({ ...baseInputs, hookConfined, hookInsideColumn, hookSideCover });
        break;
      case 'compression':
        res = calcCompression({ ...baseInputs, compressionConfined });
        break;
      case 'tension_splice':
        res = calcTensionSplice({ ...baseInputs, spliceClass });
        break;
      case 'compression_splice':
        res = calcCompressionSplice(baseInputs);
        break;
      default:
        return;
    }
    setResult(res);
  };

  const handleExportPDF = () => {
    if (!result) return;
    generateDevLengthPDF(result, {}, { projectName, engineerName, projectNumber });
  };

  const renderInputs = () => {
    return (
      <div>
        {/* Common Inputs */}
        <div style={styles.card}>
          <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Bar & Material Properties</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Bar Size</label>
              <select style={styles.select} value={barSize} onChange={e => { setBarSize(e.target.value); setResult(null); }}>
                {Object.keys(REBAR).map(k => <option key={k} value={k}>#{k} (db = {REBAR[k].db} in.)</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Yield Strength, fy (psi)</label>
              <select style={styles.select} value={fy} onChange={e => { setFy(Number(e.target.value)); setResult(null); }}>
                <option value={40000}>40,000 psi (Grade 40)</option>
                <option value={60000}>60,000 psi (Grade 60)</option>
                <option value={80000}>80,000 psi (Grade 80)</option>
                <option value={100000}>100,000 psi (Grade 100)</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Concrete Strength, f'c (psi)</label>
              <select style={styles.select} value={fc} onChange={e => { setFc(Number(e.target.value)); setResult(null); }}>
                {[3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000].map(v => <option key={v} value={v}>{v.toLocaleString()} psi</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={lightweight} onChange={e => { setLightweight(e.target.checked); setResult(null); }} />
              Lightweight concrete (Lam = 0.75)
            </label>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={coating === 'epoxy'} onChange={e => { setCoating(e.target.checked ? 'epoxy' : 'uncoated'); setResult(null); }} />
              Epoxy-coated reinforcement
            </label>
            {(calcType === 'straight_tension' || calcType === 'tension_splice') && (
              <label style={styles.checkbox}>
                <input type="checkbox" checked={topBar} onChange={e => { setTopBar(e.target.checked); setResult(null); }} />
                Top bar (> 12 in. fresh concrete below)
              </label>
            )}
          </div>
        </div>

        {/* Type-specific inputs */}
        {(calcType === 'straight_tension' || calcType === 'tension_splice') && (
          <div style={styles.card}>
            <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Development Length Method</h3>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <button style={styles.btn(useSimplified ? 'primary' : 'ghost')} onClick={() => { setUseSimplified(true); setResult(null); }}>
                Simplified (Table 25.4.2.3)
              </button>
              <button style={styles.btn(!useSimplified ? 'primary' : 'ghost')} onClick={() => { setUseSimplified(false); setResult(null); }}>
                General (Eq. 25.4.2.4a)
              </button>
            </div>

            {useSimplified ? (
              <div>
                <label style={styles.label}>Spacing & Cover Condition</label>
                <select style={styles.select} value={simplifiedCategory} onChange={e => { setSimplifiedCategory(e.target.value); setResult(null); }}>
                  <option value="category1">Category 1: Clear spacing &gt;= db, cover &gt;= db, with min. stirrups -- OR -- spacing &gt;= 2db, cover &gt;= db</option>
                  <option value="category2">Category 2: Other cases</option>
                </select>
              </div>
            ) : (
              <div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Clear Cover (in.)</label>
                    <input type="number" style={styles.input} value={clearCover} step="0.25" onChange={e => { setClearCover(parseFloat(e.target.value) || ''); setResult(null); }} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Clear Spacing (in.)</label>
                    <input type="number" style={styles.input} value={clearSpacing} step="0.25" onChange={e => { setClearSpacing(parseFloat(e.target.value) || ''); setResult(null); }} />
                  </div>
                </div>
                <label style={{ ...styles.checkbox, marginTop: '0.75rem' }}>
                  <input type="checkbox" checked={useKtr} onChange={e => { setUseKtr(e.target.checked); setResult(null); }} />
                  Include transverse reinforcement (Ktr)
                </label>
                {useKtr && (
                  <div style={{ ...styles.formGrid, marginTop: '0.75rem' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Atr - area of transverse reinf. (sq.in.)</label>
                      <input type="number" style={styles.input} value={Atr} step="0.01" onChange={e => { setAtr(parseFloat(e.target.value) || ''); setResult(null); }} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>s - transverse spacing (in.)</label>
                      <input type="number" style={styles.input} value={sTrans} step="1" onChange={e => { setSTrans(parseFloat(e.target.value) || ''); setResult(null); }} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>n - bars being developed</label>
                      <input type="number" style={styles.input} value={nBars} step="1" onChange={e => { setNBars(parseInt(e.target.value) || ''); setResult(null); }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {calcType === 'tension_splice' && (
              <div style={{ marginTop: '1rem' }}>
                <label style={styles.label}>Splice Class (Table 25.5.2.1)</label>
                <select style={styles.select} value={spliceClass} onChange={e => { setSpliceClass(e.target.value); setResult(null); }}>
                  <option value="A">Class A: As,provided/As,required &gt;= 2.0 and &lt;= 50% spliced (1.0 x ld)</option>
                  <option value="B">Class B: All other cases (1.3 x ld)</option>
                </select>
              </div>
            )}
          </div>
        )}

        {calcType === 'hook_tension' && (
          <div style={styles.card}>
            <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Hook Parameters (Table 25.4.3.2)</h3>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Side Cover Normal to Hook Plane (in.)</label>
                <input type="number" style={styles.input} value={hookSideCover} step="0.25" onChange={e => { setHookSideCover(parseFloat(e.target.value) || ''); setResult(null); }} />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.75rem' }}>
              <label style={styles.checkbox}>
                <input type="checkbox" checked={hookConfined} onChange={e => { setHookConfined(e.target.checked); setResult(null); }} />
                Confining reinforcement present (Ath >= 0.4Ahs or s >= 6db) -- Psi_r = 1.0
              </label>
              <label style={styles.checkbox}>
                <input type="checkbox" checked={hookInsideColumn} onChange={e => { setHookInsideColumn(e.target.checked); setResult(null); }} />
                Hook terminates inside column core with side cover >= 2.5 in. -- Psi_o = 1.0
              </label>
            </div>
          </div>
        )}

        {calcType === 'compression' && (
          <div style={styles.card}>
            <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Compression Parameters (Table 25.4.9.3)</h3>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={compressionConfined} onChange={e => { setCompressionConfined(e.target.checked); setResult(null); }} />
              Enclosed within spiral, circular tie, No. 4 ties at {'<='} 4 in., or hoops at {'<='} 4 in. -- Psi_r = 0.75
            </label>
          </div>
        )}

        <button style={{ ...styles.btn('primary'), width: '100%', padding: '0.75rem', fontSize: '1rem', marginTop: '0.5rem' }} onClick={calculate}>
          Calculate Development Length
        </button>
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    if (result.error) {
      return (
        <div style={{ ...styles.card, borderColor: COLORS.danger, marginTop: '1rem' }}>
          <p style={{ color: COLORS.danger, fontWeight: 600, margin: 0 }}>{result.error}</p>
        </div>
      );
    }

    return (
      <div style={{ marginTop: '1rem' }}>
        {/* Primary Result */}
        <div style={styles.resultBox}>
          <div style={styles.resultLabel}>Required {result.type.includes('Splice') ? 'Splice' : 'Development'} Length</div>
          <div style={styles.resultValue}>{result.ld.toFixed(2)} in.</div>
          <div style={styles.resultSecondary}>{(result.ld / 12).toFixed(2)} ft</div>
          <div style={{ fontSize: '0.8rem', color: COLORS.textMuted, marginTop: '0.5rem' }}>
            {result.ld_calc < result.ld_min
              ? `Minimum of ${result.ld_min} in. governs (calculated: ${result.ld_calc.toFixed(2)} in.)`
              : `Calculated value governs (min: ${result.ld_min} in.)`
            }
          </div>
        </div>

        {/* Factor Summary */}
        <div style={{ ...styles.card, marginTop: '1rem' }}>
          <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Modification Factors</h3>
          {result.factors.lambda !== undefined && (
            <div style={styles.factorRow}><span>Lightweight (Lam.)</span><span style={{ fontWeight: 700 }}>{result.factors.lambda.toFixed(2)}</span></div>
          )}
          {result.factors.psi_t !== undefined && (
            <div style={styles.factorRow}><span>Casting Position (Psi_t)</span><span style={{ fontWeight: 700 }}>{result.factors.psi_t.toFixed(2)}</span></div>
          )}
          {result.factors.psi_e !== undefined && (
            <div style={styles.factorRow}><span>Coating (Psi_e)</span><span style={{ fontWeight: 700 }}>{result.factors.psi_e.toFixed(2)}</span></div>
          )}
          {result.factors.psi_s !== undefined && (
            <div style={styles.factorRow}><span>Size (Psi_s)</span><span style={{ fontWeight: 700 }}>{result.factors.psi_s.toFixed(2)}</span></div>
          )}
          {result.factors.psi_g !== undefined && (
            <div style={styles.factorRow}><span>Grade (Psi_g)</span><span style={{ fontWeight: 700 }}>{result.factors.psi_g.toFixed(2)}</span></div>
          )}
          {result.factors.psi_r !== undefined && (
            <div style={styles.factorRow}><span>Confining Reinf. (Psi_r)</span><span style={{ fontWeight: 700 }}>{result.factors.psi_r.toFixed(2)}</span></div>
          )}
          {result.factors.psi_o !== undefined && (
            <div style={styles.factorRow}><span>Location (Psi_o)</span><span style={{ fontWeight: 700 }}>{result.factors.psi_o.toFixed(2)}</span></div>
          )}
          {result.factors.psi_c !== undefined && (
            <div style={styles.factorRow}><span>Concrete Strength (Psi_c)</span><span style={{ fontWeight: 700 }}>{result.factors.psi_c.toFixed(2)}</span></div>
          )}
          {result.factors.confinement !== undefined && (
            <>
              <div style={styles.factorRow}><span>(cb + Ktr)/db</span><span style={{ fontWeight: 700 }}>{result.factors.confinement.toFixed(3)}</span></div>
            </>
          )}
        </div>

        {/* Calculation Steps */}
        <div style={{ ...styles.card, marginTop: '1rem' }}>
          <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Calculation Reference</h3>
          {result.steps.map((step, i) => (
            <div key={i} style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#f1f5f9' }}>{step.label}</div>
              <div style={{ fontSize: '0.85rem', color: COLORS.textMid, fontFamily: 'monospace', margin: '0.25rem 0' }}>{step.eq}</div>
              <div style={{ fontSize: '0.78rem', color: COLORS.textMuted }}>Ref: {step.ref}</div>
            </div>
          ))}
          <div style={{ fontSize: '0.85rem', color: COLORS.textMid, marginTop: '0.5rem' }}>
            Method: {result.method}
          </div>
        </div>

        {/* PDF Export */}
        <div style={{ ...styles.card, marginTop: '1rem', background: 'rgba(30,58,95,0.1)', borderColor: 'rgba(30,58,95,0.25)' }}>
          <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Calculation Package</h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={styles.label}>Project Name</label>
              <input type="text" style={styles.input} value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Enter project name" />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={styles.label}>Engineer</label>
              <input type="text" style={styles.input} value={engineerName} onChange={e => setEngineerName(e.target.value)} placeholder="Enter engineer name" />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={styles.label}>Project Number</label>
              <input type="text" style={styles.input} value={projectNumber} onChange={e => setProjectNumber(e.target.value)} placeholder="Optional" />
            </div>
          </div>
          <button style={{ ...styles.btn('navy'), display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={handleExportPDF}>
            Export Calculation Package (PDF)
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.outerWrap}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitle}>Development Length Calculator</h1>
            <p style={styles.headerSub}>ACI 318-19, Chapter 25 &bull; Development lengths and lap splices</p>
          </div>
          <button style={styles.backBtn} onClick={onBack}>&larr; Back to Dashboard</button>
        </div>

        {/* Calculation Type Tabs */}
        <div style={styles.tabRow}>
          {calcTypes.map(ct => (
            <button
              key={ct.id}
              style={styles.tab(calcType === ct.id)}
              onClick={() => { setCalcType(ct.id); setResult(null); }}
            >
              {ct.label}
            </button>
          ))}
        </div>

        {/* Info Box */}
        <div style={styles.infoBox}>
          {calcType === 'straight_tension' && 'Calculate development length for deformed bars in tension per ACI 25.4.2. Choose between the simplified tabular method (Table 25.4.2.3) or the general equation (Eq. 25.4.2.4a).'}
          {calcType === 'hook_tension' && 'Calculate development length for standard hooks (90-deg and 180-deg) in tension per ACI 25.4.3. Measured from the critical section to the outside end of the hook.'}
          {calcType === 'compression' && 'Calculate development length for deformed bars in compression per ACI 25.4.9. Shorter lengths are permitted because flexural tension cracks are not present.'}
          {calcType === 'tension_splice' && 'Calculate tension lap splice length per ACI 25.5.2. Based on development length multiplied by 1.0 (Class A) or 1.3 (Class B) depending on splice conditions.'}
          {calcType === 'compression_splice' && 'Calculate compression lap splice length per ACI 25.5.5. Not permitted for bars larger than No. 11.'}
        </div>

        {renderInputs()}
        {renderResult()}
      </div>
    </div>
  );
}
